import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import {
  Building2, User, Lock, Loader2, LogIn, ArrowRight, CheckCircle2, LayoutDashboard, Eye, EyeOff
} from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import { completeVendorLogin } from '../services/vendorAuth'
import '../App.css'

type LoginPhase = 'idle' | 'submitting' | 'success'

const REDIRECT_DELAY_MS = 2200

const VendorLogin = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const isBooking = location.pathname.startsWith('/booking')
  const forgotPasswordPath = isBooking ? '/booking/forget-password' : '/vendor/forget-password'

  const [formData, setFormData] = useState({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle')
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const passwordResetDone = Boolean(
    (location.state as { passwordReset?: boolean } | null)?.passwordReset
  )

  useEffect(() => {
    const urlUsername = searchParams.get('username')
    const urlPassword = searchParams.get('password')

    if (urlUsername) {
      setFormData(prev => ({
        ...prev,
        username: decodeURIComponent(urlUsername),
        password: urlPassword ? decodeURIComponent(urlPassword) : prev.password,
      }))
      if (urlPassword) {
        setSuccess('Login credentials loaded from email. Click Sign In to continue.')
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (passwordResetDone) {
      setSuccess('Password reset successful. Sign in with your new password.')
    }
  }, [passwordResetDone])

  useEffect(() => {
    if (loginPhase !== 'success' || !redirectUrl) return

    const timer = window.setTimeout(() => {
      window.location.href = redirectUrl
    }, REDIRECT_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [loginPhase, redirectUrl])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoginPhase('submitting')

    try {
      const url = await completeVendorLogin(formData.username, formData.password)
      setRedirectUrl(url)
      setLoginPhase('success')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Please check your credentials.'
      setError(message)
      setLoginPhase('idle')
    }
  }

  const isBusy = loginPhase !== 'idle'

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-500/20 mb-4">
            <Building2 className="h-8 w-8 text-primary-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Host Login</h1>
          <p className="text-gray-300 max-w-xl mx-auto">
            Sign in to manage your properties, rooms, and bookings on AonlaStay.
          </p>
        </div>
      </section>

      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}
          {success && loginPhase === 'idle' && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              {success}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-primary-500 px-8 py-5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <LogIn className="h-5 w-5" />
                Hotel Login
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary-500" />
                    Username <span className="text-red-500">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isBusy}
                  placeholder="Enter your username"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-primary-500" />
                      Password <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <Link
                    to={forgotPasswordPath}
                    className="text-sm text-primary-600 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isBusy}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    disabled={isBusy}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isBusy}
                className="w-full bg-primary-500 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loginPhase === 'submitting' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row items-center justify-end gap-2 text-sm">
                <Link to="/vendor/signup" className="text-primary-600 hover:underline font-medium">
                  Become a host
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      {loginPhase === 'submitting' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in-overlay">
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-4 border-primary-500/20 rounded-full" />
                <div className="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-base font-semibold text-gray-800">Verifying credentials...</p>
              <p className="text-sm text-gray-500">Please wait a moment</p>
            </div>
          </div>
        </div>
      )}

      {loginPhase === 'success' && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in-overlay">
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full mx-4 text-center">
            <div className="flex flex-col items-center gap-5">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center animate-success-pop">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                  <LayoutDashboard className="h-5 w-5 text-white" />
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-xl font-bold text-gray-900">Welcome back!</h3>
                <p className="text-sm text-gray-600">
                  Login successful. Taking you to your dashboard...
                </p>
              </div>

              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full animate-progress-fill" />
              </div>

              <p className="text-xs text-gray-400 flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" />
                AonlaStay Host Dashboard
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorLogin
